import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import { AllianceBrand } from './alliance-app-bar';
import { APP_BAR, DMD_BRAND } from './alliance-tokens';
import { BarButton } from './bar-controls';
import { CURRENT_APP } from './current-app';

/** Sidebar width for both DMD apps */
export const SIDEBAR_WIDTH = 280;

/**
 * White sidebar with a cool-grey band behind the Impersonation Search
 * (DMD App System, "sidebar" and "search band" tokens).
 */
export const SIDEBAR_COLORS = {
  background: DMD_BRAND.sidebar.bg,
  border: DMD_BRAND.sidebar.line,
  strip: DMD_BRAND.searchBand,
};

/**
 * The navigation drawer: a permanent 280px white sidebar at md+ and the same
 * content in a temporary drawer below md.
 *
 * The navy header strip is the bar's height: the compact gold lockup and the
 * app name on desktop, the lockup and a close button on phones.
 *
 * @param open - Temporary drawer open state (below md)
 * @param onClose - Closes the temporary drawer
 * @param permanent - Render the permanent drawer at md+ (default: sidebar apps)
 * @param width - Drawer width (default 280)
 * @param homeTo - The header brand links to this route and closes the drawer
 *   (top-nav apps: their Home page)
 * @param topOffset - The permanent drawer starts this many px below the top
 *   of the window (Dashboard: the ad banner carousel above the bar)
 * @param children - Drawer body under the header
 */
export function AppNavDrawer(props: {
  open: boolean;
  onClose: () => void;
  permanent?: boolean;
  width?: number;
  homeTo?: string;
  topOffset?: number;
  children: React.ReactNode;
}) {
  const width = props.width ?? SIDEBAR_WIDTH;
  const topOffset = props.topOffset ?? 0;
  const permanent = props.permanent ?? CURRENT_APP.layout === 'sidebar';

  const body = (compact: boolean) => (
    <Box
      sx={{
        backgroundColor: SIDEBAR_COLORS.background,
        letterSpacing: 'normal',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${SIDEBAR_COLORS.border}`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          height: compact ? APP_BAR.heightMobile : APP_BAR.heightDesktop,
          flex: 'none',
          backgroundColor: CURRENT_APP.bar,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          p: '0 12px 0 20px',
          borderRight: '1px solid #ffffff1a',
          // Cover the drawer's border so the header meets the bar
          mr: '-1px',
        }}
      >
        <AllianceBrand
          compact={compact}
          showName={!compact}
          homeTo={props.homeTo}
          onClick={compact ? props.onClose : undefined}
        />
        <Box sx={{ flex: 1 }} />
        {compact ? (
          <BarButton
            square
            aria-label="Close navigation"
            onClick={props.onClose}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </BarButton>
        ) : null}
      </Box>

      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {props.children}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        width: permanent ? { md: width } : 0,
        flexShrink: { md: 0 },
      }}
    >
      <Drawer
        variant="temporary"
        open={props.open}
        onClose={props.onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: permanent ? 'none' : 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            border: 'none',
            boxShadow: '0 16px 40px #00000059',
          },
        }}
      >
        {body(true)}
      </Drawer>

      {permanent ? (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              border: 'none',
              width,
              top: topOffset,
              height: `calc(100% - ${topOffset}px)`,
            },
          }}
        >
          {body(false)}
        </Drawer>
      ) : null}
    </Box>
  );
}
